import api from './api';

/**
 * Enrollment Service
 * Handles enrollment-related API calls
 * Model: student (ref Student), course (ref Course), enrollmentDate
 */

const enrollmentService = {
  /**
   * Get all enrollments (backend may populate student & course)
   * @returns {Promise<Array>} - List of enrollments
   */
  getAllEnrollments: async () => {
    try {
      const response = await api.get('/enrollments');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get enrollment by ID
   * @param {string} id - Enrollment ID
   * @returns {Promise<Object>} - Enrollment object
   */
  getEnrollmentById: async (id) => {
    try {
      const response = await api.get(`/enrollments/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Create new enrollment
   * @param {Object} enrollmentData - { student: StudentId, course: CourseId, enrollmentDate?: Date }
   * @returns {Promise<Object>} - Created enrollment
   */
  createEnrollment: async (enrollmentData) => {
    try {
      const response = await api.post('/enrollments', enrollmentData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Update enrollment
   * @param {string} id - Enrollment ID
   * @param {Object} enrollmentData - Data to update
   * @returns {Promise<Object>} - Updated enrollment
   */
  updateEnrollment: async (id, enrollmentData) => {
    try {
      const response = await api.put(`/enrollments/${id}`, enrollmentData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Delete enrollment
   * @param {string} id - Enrollment ID
   * @returns {Promise<Object>} - Deletion confirmation
   */
  deleteEnrollment: async (id) => {
    try {
      const response = await api.delete(`/enrollments/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get enrollments by student ID
   * @param {string} studentId - Student ID
   * @returns {Promise<Array>} - Enrollments for that student
   */
  getEnrollmentsByStudent: async (studentId) => {
    try {
      const enrollments = await enrollmentService.getAllEnrollments();
      return enrollments.filter(
        (e) => (e.student?._id || e.student) === studentId
      );
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get enrollments by course ID
   * @param {string} courseId - Course ID
   * @returns {Promise<Array>} - Enrollments for that course
   */
  getEnrollmentsByCourse: async (courseId) => {
    try {
      const enrollments = await enrollmentService.getAllEnrollments();
      return enrollments.filter(
        (e) => (e.course?._id || e.course) === courseId
      );
    } catch (error) {
      throw error;
    }
  },
};

export default enrollmentService;
